import { isValidElement, useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import {
  formatGroupSalesNumber,
  groupSalesStatusMessage,
  isGroupSalesPeriodType,
  type GroupSalesSummary,
} from '@/utils/approval/groupSales';
import {
  GroupSalesExpandSections,
  GroupSalesExpandToggle,
} from './GroupSalesExpand';
import { GroupSalesRoot } from './styles';

const TABLE_CLASS =
  'table table-bordered table-striped table-responsive no-margin';

type TablesProps = {
  summary: GroupSalesSummary;
  /** 작성 화면·레거시 문서 조회에만 — 저장 HTML 에 넣으면 결재 뒤에도 작성 시점 상태로 남음 */
  showStatus?: boolean;
  t: TFunction;
};

/** 레거시 appr_sub_view_forms_groupsales 의 표 부분(펼침 버튼 제외) */
const GroupSalesTables = ({ summary, showStatus, t }: TablesProps) => {
  const header = summary.header ?? {};
  const isPeriod = isGroupSalesPeriodType(summary.header);
  const cols = isPeriod ? 3 : 4;
  const num = formatGroupSalesNumber;

  const amountTotal = summary.amountRows.reduce(
    (acc, row) => ({
      qty: acc.qty + (Number(row.quantity) || 0),
      amount: acc.amount + (Number(row.amount) || 0),
      discountAmount: acc.discountAmount + (Number(row.discountAmount) || 0),
      saleAmount: acc.saleAmount + (Number(row.saleAmount) || 0),
    }),
    { qty: 0, amount: 0, discountAmount: 0, saleAmount: 0 },
  );

  return (
    <>
      {summary.header && (
        <div>
          <table className={TABLE_CLASS}>
            <colgroup>
              {Array.from({ length: cols }, (_, i) => (
                <col key={i} width={isPeriod ? '33%' : '25%'} />
              ))}
            </colgroup>
            <tbody>
              <tr>
                <th className="text-center bg-info" colSpan={cols}>
                  {t('{{brand}} 단체판매 진행의 건', {
                    brand: header.brandNameEn ?? '',
                  })}
                  {showStatus && (
                    <>
                      {' ('}
                      <span className="group-sales-status">
                        {t(groupSalesStatusMessage(header.approvalStatus))}
                      </span>
                      {')'}
                    </>
                  )}
                </th>
              </tr>
              {isPeriod ? (
                <>
                  <tr>
                    <th className="text-center">{t('매장명')}</th>
                    <th className="text-center">{t('매장코드')}</th>
                    <th className="text-center">{t('판매기간 예정일')}</th>
                  </tr>
                  <tr>
                    <td className="text-center">{header.shopName}</td>
                    <td className="text-center">{header.shopCode}</td>
                    <td className="text-center">
                      {`${header.dateFrom ?? ''} ~ ${header.dateTo ?? ''} (`}
                      {t('{{count}}일', { count: Number(header.dateGap) || 0 })}
                      {')'}
                    </td>
                  </tr>
                  <tr>
                    <th className="text-center">{t('고객유형')}</th>
                    <th className="text-center">{t('납품처')}</th>
                    <th className="text-center">{t('1인당 할인금액')}</th>
                  </tr>
                  <tr>
                    <td className="text-center">{header.balanceTypeName}</td>
                    <td className="text-center">{header.organizationName}</td>
                    <td className="text-center">
                      {num(header.maxDiscountAmount)}
                    </td>
                  </tr>
                </>
              ) : (
                <>
                  <tr>
                    <th className="text-center" colSpan={2}>
                      {t('매장명')}
                    </th>
                    <th className="text-center">{t('매장코드')}</th>
                    <th className="text-center">{t('판매 예정일')}</th>
                  </tr>
                  <tr>
                    <td className="text-center" colSpan={2}>
                      {header.shopName}
                    </td>
                    <td className="text-center">{header.shopCode}</td>
                    <td className="text-center">{header.requestDate}</td>
                  </tr>
                  <tr>
                    <th className="text-center">{t('고객유형')}</th>
                    <th className="text-center">{t('판매가 유형')}</th>
                    <th className="text-center">{t('납품처')}</th>
                    <th className="text-center">{t('담당자')}</th>
                  </tr>
                  <tr>
                    <td className="text-center">{header.balanceTypeName}</td>
                    <td className="text-center">{header.saleTypeName}</td>
                    <td className="text-center">{header.organizationName}</td>
                    <td className="text-center">
                      {header.clientName
                        ? `${header.clientName} (${header.contact ?? ''})`
                        : ''}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
      {isPeriod && summary.periodRows.length > 0 && (
        <div>
          <table className={TABLE_CLASS}>
            <tbody>
              <tr>
                <th className="text-left" colSpan={7}>
                  {t('기간단체 내역')}
                </th>
              </tr>
              <tr>
                <td className="text-center">{t('업체명')}</td>
                <td className="text-center">{t('사업자번호')}</td>
                <td className="text-center">{t('인원수')}</td>
                <td className="text-center">{t('TAG가')}</td>
                <td className="text-center">{t('할인율')}</td>
                <td className="text-center">{t('할인금액')}</td>
                <td className="text-center">{t('실판매가')}</td>
              </tr>
              {summary.periodRows.map((row, i) => (
                <tr key={i}>
                  <td className="text-center">{row.organizationName}</td>
                  <td className="text-center">{row.businessNumber}</td>
                  <td className="text-center">{num(row.count)}</td>
                  <td className="text-right">{num(row.amount)}</td>
                  <td className="text-center">{`${
                    row.discountRate ?? ''
                  }%`}</td>
                  <td className="text-right">{num(row.discountAmount)}</td>
                  <td className="text-right">{num(row.saleAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!isPeriod && summary.amountRows.length > 0 && (
        <div>
          <table className={TABLE_CLASS}>
            <tbody>
              <tr>
                <th className="text-center">{t('시즌')}</th>
                <th className="text-center">{t('스타일')}</th>
                <th className="text-center">{t('단가')}</th>
                <th className="text-center">{t('현단가')}</th>
                <th className="text-center">{t('수량')}</th>
                <th className="text-center">{t('TAG가')}</th>
                <th className="text-center">{t('할인율')}</th>
                <th className="text-center">{t('할인금액')}</th>
                <th className="text-center">{t('실판매가')}</th>
              </tr>
              {summary.amountRows.map((row, i) => (
                <tr key={i}>
                  <th className="text-center" colSpan={4}>
                    {t('계')}
                  </th>
                  <td className="text-center">{num(row.quantity)}</td>
                  <td className="text-right">{num(row.amount)}</td>
                  <td className="text-center">{`${
                    row.discountRate ?? ''
                  }%`}</td>
                  <td className="text-right">{num(row.discountAmount)}</td>
                  <td className="text-right">{num(row.saleAmount)}</td>
                </tr>
              ))}
              <tr>
                <th className="text-center" colSpan={4}>
                  {t('합계')}
                </th>
                <th className="text-center">{num(amountTotal.qty)}</th>
                <th className="text-right">{num(amountTotal.amount)}</th>
                <th className="text-center" />
                <th className="text-right">
                  {num(amountTotal.discountAmount)}
                </th>
                <th className="text-right">{num(amountTotal.saleAmount)}</th>
              </tr>
              <tr>
                <th className="text-right" colSpan={9}>
                  {t('총 {{count}} 스타일', {
                    count: Number(summary.styleCount) || 0,
                  })}
                </th>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {!isPeriod && Number(header.saleTypeCount) > 0 && (
        <div>
          <p className="group-sales-notice">
            {t(
              '* 주의 : 균일/기획 상품이 혼재되어 있을 경우 유형별(세일가,균일/기획)로 할인권이 발행됩니다.',
            )}
          </p>
          <p className="group-sales-notice">
            {t('* 할인권이 2장 발행됨을 매장에 안내하여 주시기 바랍니다.')}
          </p>
        </div>
      )}
    </>
  );
};

/** 작성 화면·레거시 문서 조회 — 현재 진행 상태 문구 포함 */
export const GroupSalesTableView = ({
  summary,
  t,
}: {
  summary: GroupSalesSummary;
  t: TFunction;
}) => (
  <GroupSalesRoot>
    <GroupSalesTables summary={summary} showStatus t={t} />
  </GroupSalesRoot>
);

/** attributes.content — 작성 시점 내역표 */
export const buildGroupSalesContentHtml = (
  summary: GroupSalesSummary,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="group-sales">
      <GroupSalesTables summary={summary} t={t} />
      <GroupSalesExpandSections summary={summary} t={t} />
    </div>,
  );

const DROP_TAGS = new Set(['style', 'script', 'link']);
const TABLE_PARTS = new Set(['table', 'colgroup', 'thead', 'tbody', 'tr']);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();

/** 저장 content 그대로 */
export const GroupSalesSnapshot = ({ content }: { content: string }) => {
  const { t } = useTranslation();
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (TABLE_PARTS.has(tagName)) {
        const kept = children.filter((c) => !isBlankText(c));
        children.splice(0, children.length, ...kept);
      }
      if (element.classList.contains('group-sales-expand')) {
        const pick = (className: string) =>
          children.find(
            (c) =>
              isValidElement<{ className?: string }>(c) &&
              (c.props.className ?? '').split(' ').includes(className),
          );
        return (
          <GroupSalesExpandToggle
            key={props.key as string}
            isAmount={element.getAttribute('data-sale-type') === 'A'}
            participants={pick('group-sales-participants')}
            detail={pick('group-sales-detail')}
            t={t}
          />
        );
      }
      return undefined;
    },
    [t],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <GroupSalesRoot>{rendered}</GroupSalesRoot>;
};

export { default as GroupSalesExpand } from './GroupSalesExpand';
export {
  buildGroupSalesMultiContentHtml,
  GroupSalesMultiPassportView,
  GroupSalesMultiSnapshot,
} from './GroupSalesMulti';
